import React, { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { useAuth } from "../../context/AuthContext";
import { KYC_UPLOAD as T } from "../../config/zinihubConfig";
import styles from "./KYCUpload.module.css";

const TOTAL_STEPS = 3;

const KYCUpload = () => {
  const { token, decodeToken } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [pendingUploadKey, setPendingUploadKey] = useState(null);

  const decodedToken = useMemo(() => decodeToken(token), [token, decodeToken]);
  const [step, setStep] = useState(1);
  const [uploads, setUploads] = useState({});
  const [bankDetails, setBankDetails] = useState({
    accountNumber: "",
    ifsc: "",
    holderName: "",
  });

  const handleUploadClick = (key) => {
    setPendingUploadKey(key);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && pendingUploadKey) {
      setUploads((prev) => ({ ...prev, [pendingUploadKey]: file.name }));
    }
    setPendingUploadKey(null);
    e.target.value = "";
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate("/hubSettings/kyc");
    }
  };

  const handleContinue = () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    }
  };

  const renderProgress = () => (
    <div className={styles.progressRow}>
      {T.STEPS.map((label, i) => {
        const stepNum = i + 1;
        const isCompleted = stepNum < step;
        const isActive = stepNum === step;
        return (
          <div key={label} className={styles.progressStep}>
            <div className={styles.progressCircleContainer} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div
                className={`${styles.progressCircle} ${isCompleted ? styles.progressCircleCompleted : isActive ? styles.progressCircleActive : styles.progressCirclePending}`}
              >
                {isCompleted ? "\u2713" : stepNum}
              </div>
              <span
                className={`${styles.progressLabel} ${isCompleted ? styles.progressLabelCompleted : isActive ? styles.progressLabelActive : styles.progressLabelPending}`}
              >
                {label}
              </span>
            </div>
            {i < TOTAL_STEPS - 1 && (
              <div
                className={`${styles.progressLine} ${stepNum <= step ? styles.progressLineCompleted : styles.progressLinePending}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  const renderUploadArea = (label, note, key) => (
    <div key={key} className={styles.uploadArea} onClick={() => handleUploadClick(key)}>
      <div className={styles.uploadIcon}>{"\ud83d\udcc4"}</div>
      <div className={styles.uploadLabel}>
        {label} <span className={styles.uploadLabelNote}>({note})</span>
      </div>
      {uploads[key] ? (
        <div className={styles.fileName}>{uploads[key]}</div>
      ) : (
        <div className={styles.uploadBtn}>{T.UPLOAD_BTN}</div>
      )}
    </div>
  );

  const renderStep1 = () => (
    <div className={styles.stepCard}>
      <div className={styles.stepTitle}>{T.STEP1_TITLE}</div>
      <div className={styles.stepDesc}>{T.STEP1_DESC}</div>
      {renderUploadArea(T.STEP1_FRONT, T.REQUIRED, "id-front")}
      {renderUploadArea(T.STEP1_BACK, T.REQUIRED, "id-back")}
    </div>
  );

  const renderStep2 = () => (
    <div className={styles.stepCard}>
      <div className={styles.stepTitle}>
        {T.STEP2_TITLE} <span className={styles.stepTitleOptional}>({T.OPTIONAL})</span>
      </div>
      <div className={styles.stepDesc}>{T.STEP2_DESC}</div>
      {renderUploadArea(T.STEP2_FILE1, T.OPTIONAL, "gst-cert")}
      {renderUploadArea(T.STEP2_FILE2, T.OPTIONAL, "shop-reg")}
    </div>
  );

  const renderStep3 = () => (
    <div className={styles.stepCard}>
      <div className={styles.stepTitle}>{T.STEP3_TITLE}</div>
      <div className={styles.stepDesc}>{T.STEP3_DESC}</div>
      <div className={styles.formField}>
        <div className={styles.formLabel}>{T.STEP3_ACCOUNT_LABEL}</div>
        <input
          className={styles.formInput}
          placeholder={T.STEP3_ACCOUNT_PLACEHOLDER}
          value={bankDetails.accountNumber}
          onChange={(e) => setBankDetails((prev) => ({ ...prev, accountNumber: e.target.value }))}
        />
      </div>
      <div className={styles.formField}>
        <div className={styles.formLabel}>{T.STEP3_IFSC_LABEL}</div>
        <input
          className={styles.formInput}
          placeholder={T.STEP3_IFSC_PLACEHOLDER}
          value={bankDetails.ifsc}
          onChange={(e) => setBankDetails((prev) => ({ ...prev, ifsc: e.target.value }))}
        />
      </div>
      <div className={styles.formField}>
        <div className={styles.formLabel}>{T.STEP3_HOLDER_LABEL}</div>
        <input
          className={styles.formInput}
          placeholder={T.STEP3_HOLDER_PLACEHOLDER}
          value={bankDetails.holderName}
          onChange={(e) => setBankDetails((prev) => ({ ...prev, holderName: e.target.value }))}
        />
      </div>
      {renderUploadArea(T.STEP3_CHEQUE_LABEL, T.REQUIRED, "cancelled-cheque")}
    </div>
  );

  if (!decodedToken) {
    return (
      <div className={styles.loadingWrap}>
        <Spin size="large" />
        <p className={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  const isLastStep = step === TOTAL_STEPS;

  return (
    <div className={styles.wrapper}>
      <div className={styles.topRow}>
        <div className={styles.backBtn} onClick={handleBack}>
          {T.BACK_BTN}
        </div>
        <div className={styles.pageTitle}>{T.PAGE_TITLE}</div>
      </div>

      {renderProgress()}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}

      <div className={styles.bottomRow}>
        <div
          className={`${styles.bottomBtn} ${styles.bottomBtnOutline}`}
          style={{ width: "50%" }}
          onClick={handleBack}
        >
          {T.BACK}
        </div>
        <div
          className={`${styles.bottomBtn} ${styles.bottomBtnPrimary}`}
          style={{ width: "50%" }}
          onClick={isLastStep ? () => navigate("/hubSettings/kyc/requested") : handleContinue}
        >
          {isLastStep ? T.SUBMIT : T.CONTINUE}
        </div>
      </div>

      {step === 2 && (
        <div className={styles.skipLink}>
          <span onClick={handleContinue}>{T.SKIP}</span>
        </div>
      )}
    </div>
  );
};

export default KYCUpload;
